import type { InfiniteData } from '@tanstack/react-query'

import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  Action,
  Collection,
  MutationReadSpaceArgs,
  Space,
  PaginatedSpace,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthTokenKey } from '../../utils/keys/authToken.keys.js'
import { getCollectionsKey } from '../../utils/keys/collection.key.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'
import { getSpaceKey, getSpacesKey } from '../../utils/keys/space.key.js'

const spaceUpdater = (old: Space): Space => ({
  ...old,
  authMemberProps: {
    ...old?.authMemberProps,
    lastReadAt: new Date().toISOString(),
    unreadPostsCount: 0,
  },
})

export const useReadSpace = (options?: {
  useMutationOptions?: UseMutationOptions<
    Action,
    ClientError,
    MutationReadSpaceArgs
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const collectionsKey = getCollectionsKey()

  const authTokenKey = getAuthTokenKey()
  const networkKey = getNetworkKey()

  return useMutation<Action, ClientError, MutationReadSpaceArgs>(
    input => client.spaces.read(input),
    {
      ...useMutationOptions,
      onMutate: variables => {
        const { spaceId } = variables
        const spaceKey = getSpaceKey({ variables: { id: spaceId } })
        const spacesKey = getSpacesKey()

        const queries = queryClient.getQueriesData<Space>(spaceKey)
        queries.forEach(([key, space]) => {
          if (space) {
            queryClient.setQueriesData(key, spaceUpdater(space))
          }
        })

        queryClient.setQueriesData<InfiniteData<PaginatedSpace>>(
          spacesKey,
          old => ({
            ...old,
            pages: old?.pages?.map(page => ({
              ...page,
              nodes: page?.nodes?.map(space =>
                space?.id === spaceId ? spaceUpdater(space) : space,
              ),
              edges: page?.edges?.map(edge => ({
                ...edge,
                node:
                  edge?.node?.id === spaceId
                    ? spaceUpdater(edge?.node)
                    : edge?.node,
              })),
            })),
          }),
        )

        queryClient.setQueriesData<Collection[]>(collectionsKey, old =>
          old?.map(collection => ({
            ...collection,
            spaces: {
              ...collection?.spaces,
              nodes: collection?.spaces?.nodes?.map(space =>
                space?.id === spaceId ? spaceUpdater(space) : space,
              ),
              edges: collection?.spaces?.edges?.map(edge => ({
                ...edge,
                node:
                  edge?.node?.id === spaceId
                    ? spaceUpdater(edge?.node)
                    : edge?.node,
              })),
            },
          })),
        )

        useMutationOptions?.onMutate?.(variables)
      },
      onSettled: (data, error, variables) => {
        const spaceKey = getSpaceKey({ variables: { id: variables?.spaceId } })
        queryClient.invalidateQueries(spaceKey)

        // Invalidate network and auth token queries since their unread posts count has changed
        queryClient.invalidateQueries(networkKey)
        queryClient.invalidateQueries(authTokenKey)
      },
    },
  )
}
